/**
 * PB-/BK-Detail · Protokoll
 *
 * Zeigt das Aktionsprotokoll vorgefiltert auf den jeweiligen CRM-Kontakt
 * (Profil-Modus `user_id`, Zuordnung User + Profile serverseitig). Wird von
 * PbDetailPage und BkDetailPage eingebunden; der Tab ist nur mit Permission
 * `protokoll.read` sichtbar.
 *
 * Batch-13 Paket E — Familienkonto-Zentrierung: hat der User ein
 * Familienkonto (`user.family_account_id`, nur PB — BK hat keines), zeigt
 * der Tab das Protokoll über den gesamten Konto-Umfang (alle Mitglieder,
 * GET /v2/admin/family-accounts/{id}/protocol) statt nur der geöffneten
 * Person — über die neue, injizierbare `fetcher`-Prop von `ProtokollTable`
 * (die allgemeine `/protokoll`-Seite und der personenbezogene Fallback ohne
 * Konto bleiben auf `fixedQuery={{ user_id }}`, unverändert).
 */
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getThemeColors } from '../../../lib/theme';
import type { V1User } from '../../../lib/v1';
import { ProtokollTable } from '../../protokoll/ProtokollTable';
import { familyAccountProtocol } from '../../../lib/familyAccountData';
import type { ActivityProtocolQuery } from '../../../lib/activityProtocol';

interface Props {
  user: V1User | null;
  dark: boolean;
}

export function ProtokollTab({ user, dark }: Props) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();

  const accountId: number | null = (user as { family_account_id?: number | null } | null)?.family_account_id ?? null;

  // Referenzstabil (useCallback) — ProtokollTable hängt die Fetch-Funktion
  // in seine Effekt-Dependency-Liste, ein neuer Wert je Render würde den
  // Request in einer Schleife wiederholen.
  const accountFetcher = useCallback((query: ActivityProtocolQuery) => {
    if (accountId == null) return Promise.reject(new Error('no_account'));
    return familyAccountProtocol.list(accountId, { page: query.page, per_page: query.per_page });
  }, [accountId]);

  if (user?.id == null) {
    return (
      <div style={{ padding: '14px 4px', fontSize: 13, color: t.muted }}>
        {tr('protokoll.tab.no_user')}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <p style={{ margin: 0, fontSize: 13, color: t.muted }}>{tr('protokoll.tab.hint')}</p>
      {accountId != null ? (
        <ProtokollTable dark={dark} fetcher={accountFetcher} compact />
      ) : (
        <ProtokollTable dark={dark} fixedQuery={{ user_id: user.id }} compact />
      )}
    </div>
  );
}
